import React from 'react';
import { motion } from 'framer-motion';
import { reveal } from '../utils/motion';
import { CaseStudyCard } from '../components/CaseStudyCard';
import { SiteHeader } from '../components/SiteHeader';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { DrawnUnderline } from '../components/DrawnUnderline';
import { caseStudies, heroMetrics } from '../data/caseStudies';
import { clientLogos } from '../data/clientLogos';
import { linkClass } from '../utils/linkClass';

// Dark grey → black, applied per line so it stays consistent across the underlined word.
const gradientText = 'bg-[linear-gradient(to_bottom,#4a4a4a,#000000)] bg-clip-text text-transparent [-webkit-box-decoration-break:clone] [box-decoration-break:clone]';
export function Home() {
  return <div className="relative min-h-screen w-full bg-white font-sans text-ink" data-id="element-141" data-mp="3kvtmbh3q5vz0">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[760px] min-[700px]:h-[900px]" style={{
      backgroundImage: 'radial-gradient(rgba(0, 0, 0, 0.14) 1px, transparent 1.5px)',
      backgroundSize: '32px 32px',
      maskImage: 'linear-gradient(to bottom, #000 55%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(to bottom, #000 55%, transparent 100%)'
    }} data-id="element-142" data-mp="3owvlxz1k0a1i" />
      <div className="relative mx-auto flex max-w-[1347px] flex-col gap-[30px] px-5 py-10 md:px-10" data-id="element-143" data-mp="2mwqq76jix02p">
        <SiteHeader data-id="element-144" data-mp="2hz8fpbpuycsl" />

        <main className="flex flex-col gap-5 min-[700px]:gap-[30px]" data-id="element-145" data-mp="2hzqg8kjjzd6b">
          <section className="flex flex-col items-start gap-6 py-4 min-[700px]:flex-row min-[700px]:items-center min-[700px]:gap-10 min-[700px]:py-10 md:py-20" data-id="element-146" data-mp="2d5w1zwmpvqem">
            <motion.img src="https://cdn.magicpatterns.com/uploads/oThdsUnkXC7FRqpUL19do3/Meikson-Square.jpg" alt="Portrait of Michael Meikson" className="aspect-square w-[160px] shrink-0 rounded-full object-cover min-[700px]:w-[200px]" initial={{
            opacity: 0,
            x: -40
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={reveal} data-id="element-147" data-mp="239sqw19w0hfj" />
            <motion.div className="flex max-w-[880px] flex-1 flex-col gap-3" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            ...reveal,
            delay: 0.05
          }} data-id="element-148" data-mp="1on3q7w3wfy7k">
              <h1 className="text-headline font-semibold text-black" data-id="element-149" data-mp="1fnh0w8og2iuv">
                <span className={gradientText} data-id="element-150" data-mp="23bqgutyz8ce3">
                  18 years of experience across B2B SaaS, fintech, and healthcare.
                </span>
              </h1>
              <p className="text-subhead text-muted" data-id="element-151" data-mp="1tm6v9gjvq4fj">
                Specialize in driving business results through{' '}
                <DrawnUnderline delay={2} data-id="element-152" data-mp="1yixci77f762j">improved usability</DrawnUnderline>.
              </p>
            </motion.div>
          </section>

          <ul aria-label="Results" className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-[30px]" data-id="element-153" data-mp="2r0sw5v651t5f">
            {heroMetrics.map((m, i) => <motion.li key={m.label} className="flex flex-col rounded-[30px] bg-positive-bg p-5 [container-type:inline-size]" initial={{
            opacity: 0,
            y: 24
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            ...reveal,
            delay: 0.15 + i * 0.06
          }} data-id="element-154" data-mp="fkixp27eeuq6" data-mpi={i}>
                <span className="whitespace-nowrap text-[clamp(22px,21cqw,40px)] font-normal leading-[1.35] tracking-[-0.02em] text-positive max-[699px]:text-[28px]" data-id="element-155" data-mp="kewo0q77ebnz">
                  {m.value}
                </span>
                <span className="text-xl leading-[27px] tracking-[-0.02em] max-[699px]:text-base max-[699px]:leading-6" data-id="element-156" data-mp="5nebj2djyz9d">
                  {m.label}
                </span>
              </motion.li>)}
          </ul>

          <motion.section aria-label="Clients" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          ...reveal,
          delay: 0.35
        }} data-id="element-157" data-mp="1h1rubstg8oij">
            <ul className="grid grid-cols-3 items-center gap-x-4 gap-y-0 min-[700px]:gap-y-2 md:grid-cols-6" data-id="element-158" data-mp="27eodnrlw2wkg">
              {clientLogos.map(logo => <li key={logo.name} className="flex items-center justify-center" data-id="element-159" data-mp="2cesx9qi3g7ew">
                  <img src={logo.src} alt={logo.name} className="block aspect-[2/1] w-full max-w-[200px] object-contain" data-id="element-160" data-mp="2he5laekpbkj2" />
                </li>)}
            </ul>
          </motion.section>

          <section aria-label="Case studies" className="flex flex-col gap-[30px]" data-id="element-161" data-mp="2qaodfol3z4el">
            {caseStudies.map(study => <CaseStudyCard key={study.id} study={study} animate data-id="element-162" data-mp="302u3gsrsvbr5" />)}
          </section>

          <section className="flex flex-col gap-[30px] pt-2" data-id="element-163" data-mp="351dp0s2heliz">
            <div className="flex flex-col gap-3" data-id="element-164" data-mp="350vohj8sdl59">
              <h2 className="text-headline font-semibold" data-id="element-165" data-mp="3a1j9noac37rk">
                AI has become the core of my design practice.
              </h2>
              <p className="text-subhead text-muted" data-id="element-166" data-mp="3e2lcef3gm1ya">
                I prototype in code and can ship working software.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-[30px] md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]" data-id="element-167" data-mp="ycv67eld08f3">
              <div className="flex flex-col gap-10" data-id="element-168" data-mp="tdibk7o8xx1y">
              <div className="flex flex-col gap-4 text-base leading-[1.6]" data-id="element-169" data-mp="tgea9y7x4prg">
                <p data-id="element-170" data-mp="ogrvhkcfz1gj">
                  My primary design tool is Magic Patterns, where I maintain working prototypes of client projects. 
                </p>
                <a href="https://project-unified-inbox-with-filtering-117.magicpatterns.app/" target="_blank" rel="noopener noreferrer" className={`w-fit text-base ${linkClass}`} data-id="element-171" data-mp="1h3cmuksx3dk3">
                  View RSI Prototype →
                </a>
              </div>
              <div className="flex flex-col gap-4 text-base leading-[1.6]" data-id="element-172" data-mp="24v1ncofw78c4">
                <p data-id="element-173" data-mp="201p7b2pa49uz">
                  I built Percelo, an AEO (Answer Engine Optimization), tool solo in about two weeks with Claude Code. 
                </p>
                <a href="https://www.percelo.com" target="_blank" rel="noopener noreferrer" className={`w-fit text-base ${linkClass}`} data-id="element-174" data-mp="26s3n9o8hg11">
                  Try Percelo →
                </a>
              </div>
              </div>
              <img src="https://cdn.magicpatterns.com/uploads/8x9mJt8jkvyUyDgp9GG3CK/Percelo-hero2.png" alt="Percelo AI visibility report open on a laptop" className="h-[325px] w-full self-start rounded-[30px] object-cover object-[50%_45%]" data-id="element-175" data-mp="3am3hoa5b7h1q" />
            </div>
          </section>

          <TestimonialsSection data-id="element-176" data-mp="36ljil116e3cy" />
        </main>

        <div className="pt-10" data-id="element-177" data-mp="zutnpid1i8a3">
          <SiteHeader as="footer" data-id="element-178" data-mp="zvv9ohvlgf6l" />
        </div>
      </div>
    </div>;
}